import React, { useState, useRef, useEffect } from 'react';
import { FiSend, FiPaperclip, FiX, FiImage, FiFile, FiUsers, FiMessageCircle, FiArrowLeft } from 'react-icons/fi';
import './Messages.css';

const Messages = () => {
  const [currentView, setCurrentView] = useState('choice'); // 'choice', 'direct', 'group'
  const [messages, setMessages] = useState([
    { id: 1, text: "Hello John! Welcome to our member portal. How can I assist you today?", sender: "admin", time: "10:30 AM" },
    { id: 2, text: "Hi! I wanted to ask about the upcoming events schedule.", sender: "user", time: "10:32 AM" },
    { id: 3, text: "Of course! I'll send you the complete schedule right away.", sender: "admin", time: "10:33 AM" }
  ]);
  const [groupMessages, setGroupMessages] = useState([
    { id: 1, text: "Welcome to the group chat everyone!", sender: "admin", time: "9:15 AM" },
    { id: 2, text: "Thanks for having me! Looking forward to collaborating.", sender: "user", time: "9:20 AM" },
    { id: 3, text: "Does anyone have the meeting notes from yesterday?", sender: "user2", time: "9:25 AM" },
    { id: 4, text: "I'll share them in the documents section.", sender: "user3", time: "9:30 AM" }
  ]);

  const [newMessage, setNewMessage] = useState('');
  const [showFileOptions, setShowFileOptions] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (currentView !== 'choice') {
      scrollToBottom();
    }
  }, [messages, groupMessages, currentView]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;

    let messageText = newMessage || (selectedFile ? `Sent a file: ${selectedFile.name}` : '');

    const newMsg = {
      id: (currentView === 'direct' ? messages.length : groupMessages.length) + 1,
      text: messageText,
      sender: 'user',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      file: selectedFile
    };

    if (currentView === 'direct') {
      setMessages([...messages, newMsg]);
    } else {
      setGroupMessages([...groupMessages, newMsg]);
    }
    
    setNewMessage('');
    setSelectedFile(null);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowFileOptions(false);
    }
  };

  const handleTextareaChange = (e) => {
    setNewMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const removeFile = () => setSelectedFile(null);
  const triggerFileInput = () => fileInputRef.current.click();

  const currentMessages = currentView === 'direct' ? messages : groupMessages;

  // Choice Screen - First thing users see
  if (currentView === 'choice') {
    return (
      <div className="chat-app">
        <div className="choice-screen">
          <div className="choice-header">
            <h1>Chat Room</h1>
            <p>Choose how you want to chat</p>
          </div>
          
          <div className="choice-cards"> 
            <div className="choice-card" onClick={() => setCurrentView('direct')}>
              <div className="card-icon direct">
                <FiMessageCircle />
              </div>
              <h3>Direct Message</h3>
              <p>Chat privately with admin support.</p>
              <div className="card-features">
                <span>✓ One-on-one conversation</span>
                <span>✓ Instant support</span>
                <span>✓ Private & secure</span>
              </div>
              <button className="choice-button">Start Chat</button>
            </div>

            <div className="choice-card" onClick={() => setCurrentView('group')}>
              <div className="card-icon group">
                <FiUsers />
              </div>
              <h3>Group Chat</h3>
              <p>Join community discussions and collaborate with team members</p>
              <div className="card-features">
                <span>✓ Team collaboration</span>
                <span>✓ Community discussions</span>
                <span>✓ Share with multiple users</span>
              </div>
              <button className="choice-button">Join Group</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chat Screen - After user chooses
  return (
    <div className="chat-app">
      <div className="chat-system">
        {/* Back Button */}
        <div className="chat-header">
          <div className="header-top">
            <p className="back-button" onClick={() => setCurrentView('choice')}>
              <FiArrowLeft />
              Back to Chat Selection
            </p>
          </div>
          <div className="admin-info">
            <div className="admin-avatar">
              {currentView === 'direct' ? 'AS' : 'GC'}
            </div>
            <div className="admin-details">
              <h3>{currentView === 'direct' ? 'Admin Support' : 'Team Collaboration'}</h3>
              <p>{currentView === 'direct' ? 'Usually replies instantly' : 'Online: 12 members'}</p>
            </div>
          </div>
        </div>

        <div className="chat-messages">
          {currentMessages.map((msg) => (
            <div key={msg.id} className={`message ${msg.sender === 'admin' ? 'admin-message' : msg.sender === 'user' ? 'user-message' : 'other-user-message'}`}>
              {currentView === 'group' && msg.sender !== 'user' && (
                <div className="message-sender">
                  {msg.sender === 'admin' ? 'Admin Support' : msg.sender === 'user2' ? 'Sarah Johnson' : msg.sender === 'user3' ? 'Mike Chen' : 'Other User'}
                </div>
              )}
              <div className="message-content">
                <p>{msg.text}</p>
                {msg.file && (
                  <div className="file-attachment">
                    <FiFile className="file-icon" />
                    <span>{msg.file.name}</span>
                  </div>
                )}
                <span className="message-time">{msg.time}</span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {selectedFile && (
          <div className="selected-file">
            <div className="file-info">
              <FiPaperclip />
              <span>{selectedFile.name}</span>
            </div>
            <button className="remove-file-btn" onClick={removeFile}>
              <FiX />
            </button>
          </div>
        )}

        <form className="chat-input-form" onSubmit={handleSendMessage}>
          <div className="input-container">
            <div style={{ position: 'relative' }}>
              <button 
                type="button" 
                className="attach-button" 
                onClick={() => setShowFileOptions(!showFileOptions)}
              >
                <FiPaperclip />
              </button>

              {showFileOptions && (
                <div className="file-options">
                  <button type="button" onClick={triggerFileInput}>
                    <FiFile />
                    <span>Document</span>
                  </button>
                  <button type="button" onClick={triggerFileInput}>
                    <FiImage />
                    <span>Photo</span>
                  </button>
                </div>
              )}
            </div>

            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={handleTextareaChange}
              placeholder={`Type your message...`}
              className="message-input"
              rows="1"
            />

            <button 
              type="submit" 
              className="send-button" 
              disabled={!newMessage.trim() && !selectedFile}
            >
              <FiSend />
            </button>
          </div>
        </form>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="image/*, .pdf, .doc, .docx, .txt"
        />
      </div>
    </div>
  );
};

export default Messages;